import React, { useState } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Alert from '../components/common/Alert';
import Loading from '../components/common/Loading';
import { CreditCard, Check } from 'lucide-react';
import api from '../services/api';

interface BenefitResult {
  associate: {
    name: string;
    card_number: string;
  };
  partner: {
    name: string;
  };
  amount: number;
  discount_amount: number;
  final_amount: number;
}

export const UseBenefit: React.FC = () => {
  const [cardNumber, setCardNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<BenefitResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    setLoading(true);

    try {
      const response = await api.post('/benefits/use', {
        card_number: cardNumber,
        partner_id: partnerId,
        amount: parseFloat(amount) * 100, // Convert to cents
      });

      setSuccess(response.data);

      // Reset form
      setCardNumber('');
      setAmount('');
      setPartnerId('');
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Erro ao processar benefício'
      );
      console.error('Benefit use error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Utilizar Benefício</h1>
        <p className="text-gray-600 mt-2">
          Registre a utilização do benefício do associado
        </p>
      </div>

      {error && (
        <Alert variant="error" message={error} className="mb-6" onClose={() => setError('')} />
      )}

      {success && (
        <Alert
          variant="success"
          className="mb-6"
          onClose={() => setSuccess(null)}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              <strong>Benefício utilizado com sucesso!</strong>
            </div>
            <div className="text-sm">
              <p>
                Associado: <strong>{success.associate.name}</strong>
              </p>
              <p>
                Cartão: <strong>{success.associate.card_number}</strong>
              </p>
              <p>
                Parceiro: <strong>{success.partner.name}</strong>
              </p>
              <p>
                Valor original:{' '}
                <strong>R$ {(success.amount / 100).toFixed(2)}</strong>
              </p>
              <p>
                Desconto:{' '}
                <strong>R$ {(success.discount_amount / 100).toFixed(2)}</strong>
              </p>
              <p className="text-green-700 font-semibold">
                Valor final:{' '}
                R$ {(success.final_amount / 100).toFixed(2)}
              </p>
            </div>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Informações da Transação
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Número do Cartão"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="0000 0000 0000 0000"
              required
              disabled={loading}
              icon={<CreditCard className="w-5 h-5 text-gray-400" />}
            />

            <Input
              label="ID do Parceiro"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              placeholder="Digite o ID do parceiro"
              required
              disabled={loading}
            />

            <Input
              label="Valor da Compra (R$)"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              disabled={loading}
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? 'Processando...' : 'Processar Benefício'}
            </Button>
          </form>
        </Card>

        <Card className="bg-blue-50">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Instruções
          </h2>
          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <h3 className="font-semibold mb-2">Como utilizar:</h3>
              <ol className="list-decimal list-inside space-y-1">
                <li>Solicite o número do cartão do associado</li>
                <li>Informe o ID do seu estabelecimento parceiro</li>
                <li>Digite o valor total da compra</li>
                <li>Clique em "Processar Benefício"</li>
              </ol>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Importante:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Verifique se o cartão está ativo</li>
                <li>O desconto será aplicado automaticamente</li>
                <li>A transação será registrada no sistema</li>
                <li>Um comprovante será gerado ao final</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8">
            <Loading />
            <p className="text-center mt-4 text-gray-700">
              Processando benefício...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
